import { useLayoutEffect, useRef, useState } from 'react';

/** Keep ordinary words intact; phrases may wrap at spaces. Extremely long
 * user text can still wrap once the readable minimum size is reached. */
export function FittedWord({ text }: { text: string }) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState<number>();

  useLayoutEffect(() => {
    const root = rootRef.current;
    const measure = measureRef.current;
    const container = root?.parentElement;
    if (!root || !measure || !container) return;
    let disposed = false;

    const fit = () => {
      if (disposed) return;
      const available = container.clientWidth;
      // Layout width ignores the entrance animation's scale on the card.
      const naturalWidth = measure.offsetWidth;
      const baseSize = Number.parseFloat(getComputedStyle(measure).fontSize);
      if (!available || !naturalWidth || !baseSize) return;
      // Слово помещается целиком — кегль не трогаем вовсе: пусть работает clamp
      // из CSS. Иначе каждый повторный замер (ResizeObserver, загрузка шрифтов)
      // уменьшал размер на полпункта, и слово «съезжало» вниз ступеньками.
      if (naturalWidth <= available - 1) {
        setFontSize(undefined);
        return;
      }
      const fitted = Math.floor(baseSize * (available - 1) / naturalWidth * 2) / 2;
      setFontSize(Math.min(baseSize, Math.max(Math.min(24, baseSize), fitted)));
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    // A breakpoint can change the base size without changing this container's
    // width. Loaded web fonts also need a fresh measurement of the actual glyphs.
    window.addEventListener('resize', fit);
    document.fonts.addEventListener('loadingdone', fit);
    void document.fonts.ready.then(fit);
    return () => {
      disposed = true;
      observer.disconnect();
      window.removeEventListener('resize', fit);
      document.fonts.removeEventListener('loadingdone', fit);
    };
  }, [text]);

  return (
    <span ref={rootRef} className="session-word-fit">
      <span style={{ fontSize }}>{text}</span>
      <span className="session-word-measure" aria-hidden="true">
        <span ref={measureRef}>{text.trim().split(/\s+/).join('\n')}</span>
      </span>
    </span>
  );
}
